import { LoginResponse } from "@/api/auth/types";
import {
  createContext,
  Dispatch,
  ReactNode,
  useContext,
  useReducer,
} from "react";

type LoginPageState = {
  adminData?: LoginResponse & {
    oldPassword: string;
  };
};

type Action =
  | { type: "SET_ADMIN_DATA"; data: LoginPageState["adminData"] }
  | { type: "CLEAR" };

type LoginPageContextType = LoginPageState & {
  setAdminData: (data: LoginPageState["adminData"]) => void;
  clear: () => void;
};

const defaultUIValues: LoginPageState = {};

const LoginPageContext = createContext<LoginPageContextType | undefined>(
  undefined
);

const UIReducer = (state: LoginPageState, action: Action): LoginPageState => {
  switch (action.type) {
    case "SET_ADMIN_DATA":
      return {
        ...state,
        adminData: action.data,
      };
    case "CLEAR":
      return {
        adminData: undefined,
      };
    default:
      return state;
  }
};

export const LoginPageContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [state, dispatch]: [LoginPageState, Dispatch<Action>] = useReducer(
    UIReducer,
    defaultUIValues
  );

  return (
    <LoginPageContext.Provider
      value={{
        ...state,
        clear: () => dispatch({ type: "CLEAR" }),
        setAdminData: (data) => dispatch({ type: "SET_ADMIN_DATA", data }),
      }}
    >
      {children}
    </LoginPageContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useLoginPageStore = () => {
  const context = useContext(LoginPageContext);
  if (!context) {
    throw new Error(
      "useLoginPageContext must be used within LoginPageContextProvider"
    );
  }
  return context;
};
